import React, { useState, useEffect, useRef } from 'react'
import { Field } from '../ui/field'
import { InputGroup } from '../ui/input-group'
import { Input, Text, InputElement, IconButton } from '@chakra-ui/react'
import { InputGroupInterface } from '@/entities/app/interfaces'
import Icon from '../shared/icons'
import formatCurrency from '@/utils/price-convert'

const InputGroupContent: React.FC<InputGroupInterface> = ({
	placeholder,
	label,
	type,
	name,
	defaultValue = '',
	isPrice = false,
	inputRef,
	change,
	disabled = false,
	selectValue,
	register,
	errors,
	readOnlyActive = false,
	required = false,
	reset = false,
	autocomplete = ''
}) => {
	const [showPassword, setShowPassword] = useState(false)
	const [inputValue, setInputValue] = useState(defaultValue)
	const fileInputRef = useRef<HTMLInputElement | null>(null)

	const [formattedValue, setFormattedValue] = useState<string>(
		isPrice && defaultValue !== ''
			? formatCurrency(defaultValue)
			: String(defaultValue)
	)

	useEffect(() => {
		if (isPrice && defaultValue !== '') {
			const initRaw = defaultValue.toString().replace(/[^\d]/g, '')
			setFormattedValue(formatCurrency(initRaw))
		}
	}, [defaultValue, isPrice])

	useEffect(() => {
		if (reset) {
		  setInputValue('')
		  setFormattedValue('')
		  if (fileInputRef.current) {
			fileInputRef.current.value = ''
		  }
		}
	  }, [reset])

	useEffect(() => {
		setInputValue(defaultValue)
	}, [defaultValue])

	const inputType =
		type === 'password' ? (showPassword ? 'text' : 'password') : type

	const togglePasswordVisibility = () => setShowPassword(!showPassword)

	const parsePrice = (value: string): string => value.replace(/[^\d]/g, '')

	const handlePriceChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		registerOnChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
	) => {
		const nextRaw = parsePrice(e.target.value)
		const nextFormatted = nextRaw ? formatCurrency(nextRaw) : ''
		setFormattedValue(nextFormatted)

		if (change) {
			const syntheticEvent = {
				...e,
				target: { ...e.target, value: nextRaw ? parseFloat(nextRaw) : '' },
			} as React.ChangeEvent<HTMLInputElement>
			change(syntheticEvent)
		}
		if (registerOnChange) registerOnChange(e)
	}

	let priceRegisterProps = {}
	if (isPrice && register && name) {
		const registration = register(name, {
			setValueAs: (v: string) => parseFloat(parsePrice(v)) || 0,
		})
		const { onChange: registerOnChange, ...otherProps } = registration
		priceRegisterProps = {
			...otherProps,
			onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
				handlePriceChange(e, registerOnChange),
		}
	}

	return (
		<InputGroup w='full' mt='4' mb='4' fontSize='lg'>
			<Field label={label} required={required} labelProps={{ fontSize: 'md' }}>
				{!isPrice && type !== 'file' && (
					<Input
						type={inputType}
						placeholder={placeholder}
						p='5'
						defaultValue={inputValue}
						key={`input-${name}-${defaultValue}`}
						fontSize='lg'
						autoComplete={autocomplete}
						outline='none'
						{...(selectValue
							? {
									onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
										const value = e.target.value;
										const convertedValue = type === 'number' ? Number(value) : value;
										selectValue(convertedValue);
									},
									ref : (el: HTMLInputElement | null) => {
										if (typeof inputRef === 'function') {
										  inputRef(el)
										}
										else if (inputRef && 'current' in inputRef) {
										  (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
										}
										fileInputRef.current = el
									  }
							  }
							: register && name
							? register(name)
							: {})}
						readOnly={readOnlyActive}
						pr={type === 'password' ? '14' : undefined}
					/>
				)}

				{!isPrice && type === 'file' && (
					<Input
						type={inputType}
						placeholder={placeholder}
						p='1.5'
						ref={(el: HTMLInputElement | null) => {
							if (typeof inputRef === 'function') {
							  inputRef(el)
							}
							else if (inputRef && 'current' in inputRef) {
							  (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
							}
							fileInputRef.current = el
						  }}
						defaultValue={inputValue}
						key={`input-${name}-${defaultValue}`}
						fontSize='lg'
						outline='none'
						readOnly={readOnlyActive}
						disabled={disabled}
						onChange={change}
						accept='image/*'
						multiple
					/>
				)}

				{isPrice && (
					<>
						<Input
							type='text'
							placeholder={placeholder}
							p='5'
							value={formattedValue}
							fontSize='lg'
							outline='none'
							readOnly={readOnlyActive}
							disabled={disabled}
							onChange={(e) => handlePriceChange(e)}
							ref={(el: HTMLInputElement | null) => {
								if (typeof inputRef === 'function') {
								  inputRef(el)
								}
								else if (inputRef && 'current' in inputRef) {
								  (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
								}
								fileInputRef.current = el
							  }}
							{...priceRegisterProps}
						/>
						<InputElement
							position='right'
							height='full'
							right='0'
							display='flex'
							alignItems='center'
							justifyContent='center'
							width='12'
							pointerEvents='none'
						>
							<Text position='absolute' top='55%'>
								CO
							</Text>
						</InputElement>
					</>
				)}

				{!isPrice && type === 'password' && (
					<InputElement
						position='right'
						height='full'
						right='0'
						display='flex'
						alignItems='center'
						justifyContent='center'
						width='12'
					>
						<IconButton
							top='20%'
							position='absolute'
							aria-label={
								showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
							}
							height='full'
							width='full'
							bg='transparent'
							onClick={togglePasswordVisibility}
							children={
								showPassword ? (
									<Icon icon='viewClose' size='lg' color='black' />
								) : (
									<Icon icon='view' size='lg' color='black' />
								)
							}
						/>
					</InputElement>
				)}

				{name && errors?.[name] && (
					<Text color='red.500' fontSize='sm' mt={1}>
						{errors[name]?.message as string}
					</Text>
				)}
			</Field>
		</InputGroup>
	)
}

export default InputGroupContent
